import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useLazyQuery } from '@apollo/client/react'
import { useQueryParams, useLocation, stringifyQuery, useHistory } from '@fs/zion-router'
import { useFeatureFlag } from '@fs/zion-flags'

import {
  adjustLifeEventRecordTypes,
  adjustQueryParamsForSearch,
  deleteFalsyValues,
  getBackwardsCompatibleParams,
  isEqualShallow,
  validateSearchCriteria,
} from 'helpers'
import { groupSearch } from 'queries'
import transl from './GroupSearchContext.transl'

export const GroupSearchContext = createContext({ groupSearchQueryParams: {} })

export function GroupSearchProvider({ children }) {
  // when true, the user will see results of only that record type and not any children of said record type
  const withLeafOnlyRecordTypesFlag = useFeatureFlag('lightyear_lightyearReact_withLeafOnlyRecordTypes')
  const [criteriaErrors, setCriteriaErrors] = useState()
  const [lastQuery, setLastQuery] = useState()
  const [loading, setLoading] = useState(true)
  // We use getAllRecordsOfType so that if a user selects a life event and leaves all record types selected we don't send all the Id's to RMS
  // and just search using the life event
  const [getAllRecordsOfType, setGetAllRecordsOfType] = useState(false)
  const initialPageLoad = useRef(true)
  const location = useLocation()
  const history = useHistory()

  const options = { errorPolicy: 'all', fetchPolicy: 'no-cache' }
  const [groupSearchQuery, { data, error }] = useLazyQuery(groupSearch, options)
  const { query, setQuery, clearQuery } = useQueryParams()

  // want place to persist across queries if no change has been made
  const [searchedPlace, setSearchedPlace] = useState(null)
  useEffect(() => {
    if (data?.getPlace !== undefined && !loading && searchedPlace?.id !== data.getPlace?.id) {
      setSearchedPlace(data.getPlace !== null ? { ...data.getPlace, labelCount: data?.search?.totalCount ?? 0 } : null)
    }
  }, [data, loading, searchedPlace])

  // There is a circular dependency if we import navigate from useRouterHelper
  // so I rewrote the function here to break that
  // Jeremy Teela, Lightyear, 1/17/2023
  const navigate = useCallback(
    ({ path = location.pathname, q, replaceState = false }) => {
      const queryString = q ? `?${stringifyQuery(q)}` : ''
      const url = path + queryString
      if (replaceState) history.replace(url)
      else history.push(url)
    },
    [history, location]
  )

  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const updateUrlQuery = useCallback(
    (newQuery) => {
      deleteFalsyValues(newQuery)
      /* second argument "true" to clear entire URL before setting the new query params */
      setQuery(newQuery, true)
    },
    [setQuery]
  )

  const doGroupSearch = useCallback(async () => {
    if (!isMounted.current) return

    const errors = validateSearchCriteria(query)

    if (errors) {
      setCriteriaErrors(transl.notEnoughSearchCriteria)
      return
    }
    setCriteriaErrors()

    const adjustedCriteria = adjustQueryParamsForSearch(query)

    // flag to hide trying out getting results based off leaf nodes, or groups that match record-type exactly
    if (withLeafOnlyRecordTypesFlag.isOn) adjustedCriteria.withLeafOnlyRecordType = true

    const variables = { searchInput: adjustedCriteria, withRework: Boolean(query.hasRework) }

    if (adjustedCriteria.place) {
      variables.placeId = adjustedCriteria.place
      variables.placeIds = [adjustedCriteria.place]
    }

    setLoading(true)

    // Do the actual back-end query
    await groupSearchQuery({ variables })
    if (isMounted.current) {
      setLoading(false)
    }
  }, [groupSearchQuery, query, withLeafOnlyRecordTypesFlag.isOn])

  /* Perform search on page load and whenever the query parameters change */
  useEffect(() => {
    const currentQuery = JSON.parse(JSON.stringify(query))
    const newQuery = getBackwardsCompatibleParams(query)

    deleteFalsyValues(currentQuery)
    deleteFalsyValues(newQuery)

    if (!isEqualShallow(currentQuery, newQuery)) {
      updateUrlQuery(newQuery)
    } else if (!isEqualShallow(lastQuery, query)) {
      setLastQuery({ ...query })
      doGroupSearch()
      localStorage.setItem('ly.images.searchResults.lastQuery', JSON.stringify(query))
    } else if (initialPageLoad.current) {
      initialPageLoad.current = false
      setLoading(false)
    }
  }, [doGroupSearch, lastQuery, query, updateUrlQuery])

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) doGroupSearch()
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [doGroupSearch])

  const groupSearchQueryParams = useMemo(() => {
    const params = { ...query }

    if (params.jiapuOnly) {
      params.volumeSetSearch = 'jiapu'
      delete params.jiapuOnly
    }

    return params
  }, [query])

  const updateGroupSearchQueryParams = useCallback(
    (input) => {
      const adjustedInput = adjustLifeEventRecordTypes({ ...groupSearchQueryParams, ...input })

      // All parameter changes should reset page number, except a page number update
      if (Object.entries(lastQuery).some(([key, value]) => key !== 'page' && adjustedInput[key] !== value)) {
        adjustedInput.page = 1
      }

      updateUrlQuery(adjustedInput)
    },
    [groupSearchQueryParams, lastQuery, updateUrlQuery]
  )

  const state = useMemo(() => {
    const setGroupSearchQueryParams = (input, path = location.pathname, replaceState = true) => {
      if (getAllRecordsOfType) input.lifeEventRecordTypes = null // delete all the lifeEventRecordTypes since all of them have been selected and we can just search by life event
      const adjustedInput = adjustLifeEventRecordTypes(input)

      if (path !== location.pathname) navigate({ path, q: adjustedInput, replaceState })
      else updateUrlQuery(adjustedInput)
    }

    return {
      adjustQueryParams: adjustQueryParamsForSearch,
      groupSearchCriteriaErrors: criteriaErrors,
      groupSearchQueryError: error,
      groupSearchQueryLoading: Boolean(loading),
      groupSearchQueryParams,
      groupSearchQueryResults: criteriaErrors ? null : data?.search,
      place: criteriaErrors ? null : searchedPlace,
      resetGroupSearchQueryParams: clearQuery,
      setGroupSearchQueryParams,
      updateGroupSearchQueryParams,
      setGetAllRecordsOfType,
    }
  }, [
    criteriaErrors,
    error,
    loading,
    groupSearchQueryParams,
    data,
    searchedPlace,
    clearQuery,
    updateGroupSearchQueryParams,
    location,
    getAllRecordsOfType,
    navigate,
    updateUrlQuery,
  ])

  return <GroupSearchContext.Provider value={state}>{children}</GroupSearchContext.Provider>
}

GroupSearchProvider.propTypes = {
  children: PropTypes.node,
}
